import { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getFunctions, httpsCallable } from "firebase/functions";
import { doc, onSnapshot, runTransaction, setDoc, getDoc, addDoc, collection, Timestamp } from "firebase/firestore";
import { getAuth, onAuthStateChanged, signOut } from "firebase/auth";
import LoginPage from './LoginPage';
import { addOrder, db, resetPots } from "./firebase";
import SettingsPage from './SettingsPage';
import { storage } from "./firebase";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import TextBox from './components/TextBox';
import BackgroundEditor from './components/BackgroundEditor';
import Nadlan from './components/Nadlan';
import WebLogoFind from './components/WebLogoFind';
import ExcelExport from './components/ExcelExport';
import PaymentOptionsScreen from './PaymentPage';
import Monit from './components/Monit'; 

// --- Constants ---
const MAX_POT_CAPACITY_LITERS = 40;
const CHOLENT_CONSUMPTION_LITERS = { "250 גרם": 0.25, "500 גרם": 0.5, "קילו": 1.0 };
const DEFAULT_MENU = [
    { id: 1, name: "צ'ולנט", isSpecialChamin: true, type: "chamin", options: [ { size: "250 גרם", basePrice: 15, meatPrice: 5, frameImage: "reva" }, { size: "500 גרם", basePrice: 25, meatPrice: 10, frameImage: "haci" }, { size: "קילו", basePrice: 60, meatPrice: 5, frameImage: "kilo" } ] },
    { id: 2, name: "יאפצ'ק בקר", isExpandable: true, type: "yaapchik", options: [ { label: "250 גרם", price: 20 }, { label: "פס יאפצ'ק", price: 55 }, { label: "500 גרם", price: 40 } ] },
    { id: 3, name: "לחמניות ויזניץ", isExpandable: true, type: "bread", options: [ { label: "המוציא", price: 4, frameImage: "hamochi" }, { label: "מזונות", price: 5, frameImage: "mezonot" }, { label: "חצי חלה", price: 12, frameImage: "hala" } ] },
    { id: 4, name: "פטה כבד", isExpandable: true, type: "liver", options: [ { label: "150 גרם", price: 20, frameImage: "kaved150" }, { label: "250 גרם", price: 30, frameImage: "kaved250" } ] },
    { id: 5, name: "שתייה", isExpandable: true, type: "drinks", options: [ { label: "סודה", price: 5, frameImage: "soda" }, { label: "פחיות", price: 7, frameImage: "pahit" }, { label: "בירות", price: 10, frameImage: "bira" } ] },
    { id: 6, name: "קוגל", isExpandable: true, type: "kugel", options: [ { label: "מנה קוגל תפוח אדמה", price: 5 }, { label: "מנה קוגל אטריות", price: 5 }, { label: "פס קוגל תפוח אדמה", price: 25 } ] },
    { id: 7, name: "מוצר", isExpandable: true, type: "custom", isEditable: true, options: [ { label: "מוצר 1", price: 10 }, { label: "מוצר 2", price: 15 }, { label: "מוצר 3", price: 20 } ] },
];

// --- Sub-Components defined OUTSIDE of the main App component ---
function SelectedItemRow({ selection, parentMenuItem, onIncrement, onDecrement, onToggleChaminOption, onStartEditingOwner, onFinishEditingOwner, onHandleOwnerTextChange, calculateSingleChaminItemPrice, onSetQuantity }) {
    const [editingQuantityMode, setEditingQuantityMode] = useState(null);
    const [manualQuantityInput, setManualQuantityInput] = useState(selection.quantity.toString());
    const handleQuantityTextClick = () => { setEditingQuantityMode('strip'); setManualQuantityInput(selection.quantity.toString()); };
    const handleQuantityTextDoubleClick = () => { setEditingQuantityMode('manual'); setManualQuantityInput(selection.quantity.toString()); };
    const handleSelectFromStrip = (num) => { onSetQuantity(selection.id, num); setEditingQuantityMode(null); };
    const handleManualQuantityInputChange = (e) => { setManualQuantityInput(e.target.value); };
    const submitManualQuantity = () => { const num = parseInt(manualQuantityInput, 10); if (!isNaN(num)) { onSetQuantity(selection.id, Math.max(1, num)); } else { setManualQuantityInput(selection.quantity.toString()); } setEditingQuantityMode(null); };
    const handleManualQuantityKeyDown = (e) => { if (e.key === 'Enter') { submitManualQuantity(); e.target.blur(); } else if (e.key === 'Escape') { setEditingQuantityMode(null); setManualQuantityInput(selection.quantity.toString()); } };
    const closeEditModes = () => { setEditingQuantityMode(null); };
const [isTakeaway, setIsTakeaway] = useState(false);

    return (
        <div className="border-t border-gray-700 pt-3 mt-3 text-white relative">
            <div className="flex flex-col md:flex-row md:flex-nowrap md:items-center md:justify-between w-full gap-y-2 md:gap-x-4 mb-2">
                <div className="flex items-center gap-x-3">
                    <div className="flex items-center gap-x-1 relative">
                        <button className="bg-gray-700 hover:bg-gray-600 text-white font-bold p-2 rounded-md text-sm leading-none transition-colors duration-150" onClick={() => onDecrement(selection.id)} disabled={editingQuantityMode !== null}>-</button>
                        {editingQuantityMode === 'manual' ? (<input type="number" value={manualQuantityInput} onChange={handleManualQuantityInputChange} onBlur={submitManualQuantity} onKeyDown={handleManualQuantityKeyDown} className="w-12 text-center bg-gray-600 text-white border border-gray-500 rounded-md p-1 text-sm focus:ring-amber-500 focus:border-amber-500" autoFocus min="1" />) : (<span className="text-base w-8 text-center font-medium cursor-pointer hover:text-amber-400 transition-colors" onClick={editingQuantityMode === 'strip' ? closeEditModes : handleQuantityTextClick} onDoubleClick={handleQuantityTextDoubleClick} title="לחץ לבחירת כמות, לחץ פעמיים להקלדה">{selection.quantity}</span>)}
                        <button className="bg-gray-700 hover:bg-gray-600 text-white font-bold p-2 rounded-md text-sm leading-none transition-colors duration-150" onClick={() => onIncrement(selection.id)} disabled={editingQuantityMode !== null}>+</button>
                    </div>
                    <span className="font-semibold text-base">{parentMenuItem?.isSpecialChamin ? `צ'ולנט ${selection.size}` : selection.label}</span>
                </div>
                <div className="flex flex-col items-stretch md:flex-row md:items-center gap-y-2 md:gap-x-3 w-full">
                    <div className="flex flex-row items-center justify-end flex-wrap gap-2 w-full md:w-auto">
                        {parentMenuItem?.isSpecialChamin && (<> <button className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors duration-150 flex-1 ${selection.withMeat ? 'bg-amber-100 text-amber-800 hover:bg-amber-200' : 'bg-gray-600 text-gray-200 hover:bg-gray-500'}`} onClick={() => onToggleChaminOption(selection.id, 'meat')}>{selection.withMeat ? "עם בשר" : "בלי בשר"}</button> <button className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors duration-150 flex-1 ${selection.withKishke ? 'bg-amber-100 text-amber-800 hover:bg-amber-200' : 'bg-gray-600 text-gray-200 hover:bg-gray-500'}`} onClick={() => onToggleChaminOption(selection.id, 'kishke')}>{selection.withKishke ? "עם קישקה" : "בלי קישקה"}</button> </>)}
                        {parentMenuItem?.isSpecialChamin && (<button className="bg-sky-600 hover:bg-sky-700 text-white rounded-full px-3 py-1.5 text-xs font-medium transition-colors duration-150 flex-1" onClick={() => onStartEditingOwner(selection.id)}>הוסף שם</button>)}
                    </div>
                    <div className="flex justify-end md:justify-start"><span className="text-lg font-bold text-amber-400 whitespace-nowrap">{parentMenuItem?.isSpecialChamin ? calculateSingleChaminItemPrice(selection.size, selection.withMeat) * selection.quantity : (selection.price || 0) * selection.quantity} ₪</span></div>
                </div>
            </div>
            {editingQuantityMode === 'strip' && (<div className="flex flex-wrap justify-center gap-1 p-2 mt-1 bg-gray-700 border border-gray-500 rounded-md shadow-lg">{Array.from({ length: 10 }, (_, i) => i + 1).map(num => (<button key={num} onClick={() => handleSelectFromStrip(num)} className="bg-amber-500 hover:bg-amber-600 text-black font-semibold p-1 w-7 h-7 flex items-center justify-center rounded-md text-xs transition-colors">{num}</button>))} <button onClick={closeEditModes} className="bg-red-500 hover:bg-red-600 text-white font-semibold p-1 w-7 h-7 flex items-center justify-center rounded-md text-xs transition-colors" title="סגור">X</button></div>)}
            {parentMenuItem?.isSpecialChamin && selection.isEditingOwner && (<div className="flex items-center mt-2 gap-x-2"><input type="text" className="border border-gray-600 bg-gray-700 text-white p-1.5 rounded w-full text-right text-sm placeholder-gray-400 focus:ring-1 focus:ring-amber-500 focus:border-amber-500" placeholder="הכנס שם לבעל המנה" value={selection.owner} onChange={(e) => onHandleOwnerTextChange(selection.id, e.target.value)} /><button className="bg-green-500 hover:bg-green-600 text-white px-3 py-1.5 rounded text-sm font-medium transition-colors duration-150" onClick={() => onFinishEditingOwner(selection.id)}>✔</button></div>)}
            {parentMenuItem?.isSpecialChamin && selection.owner && !selection.isEditingOwner && (<div className="text-gray-400 text-sm text-right mt-1"><span className="font-semibold text-gray-300">של:</span> {selection.owner}</div>)}
        </div>
    );
}

// /// חלק 4: MenuCategoryItem ///
function MenuCategoryItem({
    item, isOpen, onToggleExpandableMenu, onAddExpandableItem, selectedItemsInCategory,
    onIncrementSelectedItem, onDecrementSelectedItem, onToggleChaminOptionItem, onStartEditingOwnerItem,
    onFinishEditingOwnerItem, onHandleOwnerTextChangeItem, calculateSingleChaminItemPriceFunc,
    cholentKashrutPreference, onSetCholentKashrutPreference, onSetItemQuantityFunc, categoryItemCount,
    mehfoodPotData, sheeritPotData, showPotsFeature,
    isEditMode, onEditCategory, onEditOption, onDeleteOption, onAddOption, onDeleteCategory
}) {

    const [showPotsStatus, setShowPotsStatus] = useState(false);
    const handleTogglePotsView = (e) => { e.stopPropagation(); setShowPotsStatus(prev => !prev); };
    const handleToggleAccordion = () => {
        if (isOpen && showPotsStatus) { setShowPotsStatus(false); }
        onToggleExpandableMenu(item.id);
    };
    let itemNameClasses = "text-xl font-semibold text-custom-menu-brown";
    if (item.isSpecialChamin || item.isExpandable) { itemNameClasses += " font-rubik-distressed"; }

    const PencilIcon = () => (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.536L16.732 3.732z" />
        </svg>
    );

    const DeleteIcon = () => (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
    );

    return (
        <div className="border border-gray-700 bg-gray-800 shadow-lg rounded-lg p-4 text-right transform transition-all duration-300 hover:shadow-amber-500/30 hover:border-amber-500/50">
            <div className="flex justify-between items-center cursor-pointer" onClick={handleToggleAccordion}>
     <div className="flex items-center gap-2">
    <span className={itemNameClasses}>{item.name}</span>
    {isEditMode && item.isEditable && (
        <div className="flex items-center">
            <button onClick={(e) => { e.stopPropagation(); onEditCategory(item.id); }} className="p-1.5 text-gray-400 rounded-full hover:bg-gray-700 hover:text-white transition-colors">
                <PencilIcon />
            </button>
            <button onClick={(e) => { e.stopPropagation(); onDeleteCategory(item.id); }} className="p-1.5 text-red-400 rounded-full hover:bg-red-500 hover:text-white transition-colors">
                <DeleteIcon />
            </button>
        </div>
    )}
</div>

                <div className="flex items-center gap-x-3">
                    {categoryItemCount > 0 && (
                        <div className="relative" title={`סה"כ ${categoryItemCount} פריטים מ${item.name}`}>
                            <img src="/cart_icon.png" alt="" className="w-6 h-6 text-gray-400" />
                            <div className="absolute -top-2 -right-2 w-5 h-5 bg-amber-300 text-amber-900 text-xs rounded-full flex items-center justify-center font-bold border-2 border-gray-800" style={{ lineHeight: '1' }}>{categoryItemCount}</div>
                        </div>
                    )}
                    <span className={`transform transition-transform duration-300 text-amber-400 ${isOpen ? "rotate-180" : "rotate-0"}`}>▼</span>
                </div>
            </div>

            {isOpen && (
                <div className="mt-4 space-y-3">
                    {item.isSpecialChamin && (
                        <div className="flex flex-col sm:flex-row sm:justify-between items-center gap-3 mb-3 py-3 border-y border-gray-700">
                            <div className="flex items-center justify-center sm:justify-start gap-x-2 w-full sm:w-auto">
                                <span className="text-sm text-gray-300">העדפת כשרות:</span>
                                <button className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors duration-150 ${cholentKashrutPreference === 'מחפוד' ? 'bg-green-500 text-white' : 'bg-gray-600 text-gray-200 hover:bg-gray-500'}`} onClick={() => onSetCholentKashrutPreference('מחפוד')}>מחפוד</button>
                                <button className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors duration-150 ${cholentKashrutPreference === 'שארית ישראל' ? 'bg-green-500 text-white' : 'bg-gray-600 text-gray-200 hover:bg-gray-500'}`} onClick={() => onSetCholentKashrutPreference('שארית ישראל')}>שארית ישראל</button>
                            </div>
                            {showPotsFeature && (
                                <button onClick={handleTogglePotsView} className="text-xs bg-sky-500 hover:bg-sky-600 text-white py-1 px-3 rounded-md transition-colors duration-150 w-full sm:w-auto order-last sm:order-none">{showPotsStatus ? "הסתר סירים" : "הצץ בסיר"}</button>
                            )}
                        </div>
                    )}
                    {item.isSpecialChamin && showPotsStatus && <CholentPotsDisplay mehfoodPotData={mehfoodPotData} sheeritPotData={sheeritPotData} />}

                    {/* === המבנה המתוקן והנכון === */}
                    {isEditMode && item.isEditable ? (
                        <div className="space-y-2">
                            {item.options.map((option, idx) => (
                                <div key={idx} className="flex items-center justify-between bg-gray-700/50 p-2 rounded-lg text-white">
                                    <div className="font-medium">{option.label} - {option.price}₪</div>
                                    <div className="flex items-center gap-2">
                                        <button onClick={() => onEditOption(item.id, idx)} className="p-1.5 text-gray-400 rounded-full hover:bg-gray-600 hover:text-white transition-colors" title="ערוך מוצר">
                                            <PencilIcon />
                                        </button>
                                        <button onClick={() => onDeleteOption(item.id, idx)} className="p-1.5 text-red-400 rounded-full hover:bg-red-500 hover:text-white transition-colors" title="מחק מוצר">
                                            <DeleteIcon />
                                        </button>
                                    </div>
                                </div>
                            ))}
                            <button onClick={() => onAddOption(item.id)} className="w-full text-center py-2 px-4 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors font-semibold mt-2">
                                + הוסף מוצר חדש
                            </button>
                        </div>
                    ) : (
                        <div className="flex flex-wrap justify-center md:justify-end gap-3 mb-4">
                            {item.options.map((option, idx) => {
                                const isScalable = (item.type === 'chamin' && (option.size === '250 גרם' || option.size === '500 גרם')) || (item.type === 'drinks' && (option.label === 'פחיות' || option.label === 'בירות'));
                                let buttonBaseClasses = "transition-transform duration-150 ease-in-out";
                                if (isScalable) { buttonBaseClasses += " hover:scale-110"; }
                                if (option.frameImage) {
                                    return (<button key={idx} className={`relative flex flex-col items-center justify-end bg-transparent border-none p-0 w-28 h-28 cursor-pointer group ${buttonBaseClasses}`} onClick={() => onAddExpandableItem(item, option)}>
                                        <img src={`/${option.frameImage}.png`} alt={option.size || option.label} className="absolute inset-0 w-full h-full object-contain group-hover:opacity-80 transition-opacity" />
                                        <span className="relative z-10 text-xs sm:text-sm font-semibold text-white mb-1.5 group-hover:text-amber-300">{option.size || option.label}</span>
                                    </button>);
                                } else {
                                    return (<button key={idx} className={`bg-amber-500 hover:bg-amber-600 text-black px-3.5 py-1.5 rounded-full text-sm font-medium shadow ${buttonBaseClasses}`} onClick={() => onAddExpandableItem(item, option)}>{option.size || option.label}</button>);
                                }
                            })}
                        </div>
                    )}
                    {/* ============================= */}

                    {selectedItemsInCategory.map((selection) => (<SelectedItemRow key={selection.id} selection={selection} parentMenuItem={item} onIncrement={onIncrementSelectedItem} onDecrement={onDecrementSelectedItem} onToggleChaminOption={onToggleChaminOptionItem} onStartEditingOwner={onStartEditingOwnerItem} onFinishEditingOwner={onFinishEditingOwnerItem} onHandleOwnerTextChange={onHandleOwnerTextChangeItem} calculateSingleChaminItemPrice={calculateSingleChaminItemPriceFunc} onSetQuantity={onSetItemQuantityFunc} />))}
                    {selectedItemsInCategory.length > 0 && (<div className="border-t border-gray-700 pt-2 mt-4 text-start"><span className="text-gray-200 font-bold">סך הכל {item.name}: {selectedItemsInCategory.reduce((sum, s) => sum + (item.isSpecialChamin ? (calculateSingleChaminItemPriceFunc(s.size, s.withMeat) * s.quantity) : ((s.price || 0) * s.quantity)), 0)} ₪</span></div>)}
                </div>
            )}
        </div>
    );
}


function EditMenuModal({ isOpen, onClose, onSave, itemData, editType }) {
    const [name, setName] = useState('');
    const [price, setPrice] = useState('');

    useEffect(() => {
        if (isOpen && itemData) {
            setName(itemData.name || itemData.label || '');
            setPrice(itemData.price !== undefined ? itemData.price.toString() : '');
        }
    }, [isOpen, itemData]);

    if (!isOpen) return null;

    const getTitle = () => {
        if (editType === 'category') return 'ערוך שם קטגוריה';
        if (editType === 'option') return 'ערוך מוצר';
        if (editType === 'new_option') return 'הוסף מוצר חדש';
        return '';
    };

    const handleSave = () => {
        const priceAsNumber = parseFloat(price);
        if (editType === 'category') {
            if (!name.trim()) { alert("שם קטגוריה לא יכול להיות ריק"); return; }
            onSave({ name });
        } else {
            if (!name.trim() || isNaN(priceAsNumber) || priceAsNumber < 0) { alert("שם ומחיר חייבים להיות תקינים"); return; }
            onSave({ label: name, price: priceAsNumber });
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex justify-center items-center z-50 p-4" onClick={onClose}>
            <div className="bg-gray-800 text-white rounded-xl p-8 shadow-2xl max-w-sm w-full text-center" onClick={e => e.stopPropagation()}>
                <h3 className="text-xl font-bold text-amber-400 mb-4">{getTitle()}</h3>
                <div className="space-y-4 text-right">
                    <input
                        className="border border-gray-600 bg-gray-700 text-white p-2.5 rounded w-full placeholder-gray-400"
                        value={name}
                        onChange={e => setName(e.target.value)}
                        placeholder={editType === 'category' ? 'שם קטגוריה' : 'שם מוצר'}
                        autoFocus
                    />
                    {editType !== 'category' && (
                        <input
                            className="border border-gray-600 bg-gray-700 text-white p-2.5 rounded w-full placeholder-gray-400"
                            value={price}
                            onChange={e => setPrice(e.target.value)}
                            placeholder="מחיר"
                            type="number"
                            min="0"
                        />
                    )}
                </div>
                <div className="flex gap-2 justify-center mt-6">
                    <button onClick={handleSave} className="bg-amber-500 hover:bg-amber-600 text-black font-bold py-2 px-6 rounded-lg transition-colors">שמור</button>
                    <button onClick={onClose} className="bg-gray-500 hover:bg-gray-600 text-white font-bold py-2 px-6 rounded-lg transition-colors">ביטול</button>
                </div>
            </div>
        </div>
    );
}

// /// חלק 5: OrderSubmitButton ///
function OrderSubmitButton({ totalPrice, onSubmitOrder, isDisabled }) {
    const baseClasses = `font-bold py-3 px-4 rounded-lg w-full transform transition-all duration-300 shadow-lg text-lg text-white disabled:cursor-not-allowed`;
    const backgroundColorClasses = "bg-blue-600 hover:bg-blue-700";
    const scaleEffect = "hover:scale-105";
    return (<button className={`${baseClasses} ${backgroundColorClasses} ${scaleEffect}`} onClick={onSubmitOrder} disabled={isDisabled}>שלם (סה"כ: {totalPrice} ₪)</button>);
}

// /// חלק 6: ConfirmationPopup ///
function ConfirmationPopup({ showPopup, popupMessage, onClosePopup }) { if (!showPopup) return null; return (<div className="fixed inset-0 w-full h-full bg-black bg-opacity-80 flex justify-center items-center z-50 p-4"><div className="bg-gray-800 text-white rounded-xl p-6 md:p-8 text-center shadow-2xl max-w-sm w-full"><h2 className="text-xl md:text-2xl font-bold mb-4 whitespace-pre-wrap">{popupMessage}</h2><button className="bg-amber-500 hover:bg-amber-600 text-black font-bold py-2 px-6 rounded-lg transition-colors duration-150 text-base" onClick={onClosePopup}>אישור</button></div></div>); }

// /// חלק 8: ConfirmReceivedDialog ///
function ConfirmReceivedDialog({ onConfirmYes, onConfirmNo }) { return (<div className="fixed inset-0 bg-black bg-opacity-75 flex justify-center items-center z-50 p-4"><div className="bg-gray-800 text-white rounded-xl p-6 md:p-8 text-center shadow-2xl max-w-sm w-full"><h2 className="text-xl md:text-2xl font-bold mb-6">האם קיבלת כבר את ההזמנה לידך?</h2><div className="flex justify-around gap-4"><button onClick={onConfirmYes} className="bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-6 rounded-lg text-lg min-w-[100px] transition-colors">כן</button><button onClick={onConfirmNo} className="bg-red-500 hover:bg-red-600 text-white font-bold py-3 px-6 rounded-lg text-lg min-w-[100px] transition-colors">לא</button></div></div></div>); }

// /// חלק 9: CustomerDetailsScreen ///
function CustomerDetailsScreen({ name, onNameChange, contact, onContactChange, method, onMethodChange, onSubmit, onReturnToOrdering, totalPrice }) {
    return (
        <div className="flex flex-col justify-center items-center py-10" style={{ minHeight: 'calc(100vh - 100px)' }}>
            <div className="w-full max-w-md p-6 bg-gray-800 bg-opacity-90 rounded-lg shadow-xl">
                <h2 className="text-2xl font-bold mb-6 text-amber-400">פרטי הזמנה וקבלת התראות</h2>
                <input className="border border-gray-600 bg-gray-700 text-white p-2.5 rounded mb-3 w-full text-right placeholder-gray-400" placeholder="* הכנס שם" value={name} onChange={onNameChange} />
                <div className="flex justify-center gap-4 my-4 text-white">
                    <label className="flex items-center gap-2 cursor-pointer">
                        <input type="radio" name="notification" value="sms" checked={method === 'sms'} onChange={onMethodChange} className="form-radio text-amber-500 focus:ring-amber-500" />
                        <span>SMS</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                        <input type="radio" name="notification" value="email" checked={method === 'email'} onChange={onMethodChange} className="form-radio text-amber-500 focus:ring-amber-500" />
                        <span>אימייל</span>
                    </label>
                </div>
                <input
                    className="border border-gray-600 bg-gray-700 text-white p-2.5 rounded mb-6 w-full text-right placeholder-gray-400"
                    placeholder={method === 'sms' ? '* מספר טלפון' : '* כתובת אימייל'}
                    value={contact}
                    onChange={onContactChange}
                    type={method === 'email' ? 'email' : 'tel'}
                />
                <div className="mt-6 space-y-3">
                    <button onClick={onSubmit} className="bg-amber-500 hover:bg-amber-600 text-black font-bold py-3 px-4 rounded-lg w-full text-lg">המשך לתשלום (סה"כ: {totalPrice} ₪)</button>
                    <button onClick={onReturnToOrdering} className="bg-gray-600 hover:bg-gray-700 text-white font-bold py-2.5 px-4 rounded-lg w-full">חזור לבחירת פריטים</button>
                </div>
            </div>
        </div>
    );
}


// /// חלק 11: MainOrderView ///
// ====================== הדבק את כל הקוד מכאן ======================
function MainOrderView({ currentTime, showPassword, passwordInput, onToggleShowPassword, onPasswordInputChange, onAdminAccessSubmit, menuData, openExpandableMenus, expandableSelections, cholentKashrut, onToggleExpandableMenu, onAddExpandableItem, onIncrementItem, onDecrementItem, onSetItemQuantity, onToggleChaminOption, onStartEditingOwner, onFinishEditingOwner, onHandleOwnerTextChange, calculateSingleChaminItemPriceFunc, onSetCholentKashrut, totalPrice, onInitiateCheckout, onNewOrder, mehfoodPotData, sheeritPotData, showPotsFeature, onLogout, isCustomerMode, isEditMode, contactLinks, setEditModal, setIsEditMode, userSettings, setUserSettings, effectiveUserId, user, onEditCategory, onEditOption, onDeleteOption, onAddOption, onAddCategory, onDeleteCategory,showTextboxFeature, storeMessage, backgroundColor, setBackgroundColor, showNadlanFeature, showLogoFinder, menuConfig, showExcelExport, showMonit }) {
    // --- לוגו/וידאו ---
    const [logoFile, setLogoFile] = useState(null);
    const [logoPreview, setLogoPreview] = useState(null);
    const [logoError, setLogoError] = useState("");
    const [isUploading, setIsUploading] = useState(false);
    useEffect(() => {
        if (userSettings && userSettings.logoUrl) {
            setLogoPreview(userSettings.logoUrl);
        } else {
            setLogoPreview(null);
        }
    }, [userSettings]);
    const handleLogoChange = (e) => {
        setLogoError("");
        const file = e.target.files[0];
        if (!file) return;
        const allowedTypes = ["image/png", "image/jpeg", "video/mp4"];
        if (!allowedTypes.includes(file.type)) {
            setLogoError("רק קבצי PNG, JPG, MP4 מותרים.");
            return;
        }
        if (file.size > 700 * 1024) {
            setLogoError("הקובץ גדול מדי (מקסימום 700KB).");
            return;
        }
        setLogoFile(file);
        setLogoPreview(URL.createObjectURL(file));
    };
   const handleUploadLogo = async () => {
    if (!user || user.uid !== effectiveUserId) {
        setLogoError("רק בעל החשבון יכול להעלות לוגו.");
        return;
    }
    if (!logoFile) return;

    setIsUploading(true);
    setLogoError("");

    const oldLogoUrl = userSettings?.logoUrl || null;
    
    const ext = logoFile.name.split('.').pop();
    const uploadPath = `users/${effectiveUserId}/logo/logo_${Date.now()}.${ext}`;
    const storageRef = ref(storage, uploadPath);

    try {
        await uploadBytes(storageRef, logoFile, { contentType: logoFile.type });
        const newUrl = await getDownloadURL(storageRef);

        const settingsDocRef = doc(db, "users", effectiveUserId, "settings", "main");
        await setDoc(settingsDocRef, { logoUrl: newUrl }, { merge: true });

        setUserSettings(prev => ({ ...prev, logoUrl: newUrl }));
        setLogoFile(null);
        setLogoPreview(newUrl);

        if (oldLogoUrl) {
            try {
                const oldLogoRef = ref(storage, oldLogoUrl);
                await deleteObject(oldLogoRef);
                console.log("Previous logo deleted successfully.");
            } catch (deleteError) {
                console.error("Failed to delete previous logo:", deleteError);
            }
        }

    } catch (err) {
        setLogoError("שגיאה בהעלאת קובץ: " + err.message);
        console.error('[DEBUG] Upload error:', err);
    } finally {
        setIsUploading(false);
    }
};
const handleRemoveLogo = async () => {
    if (!effectiveUserId || !(userSettings?.logoUrl)) return;
    
    setIsUploading(true);
    setLogoError("");

    try {
        const storageRef = ref(storage, userSettings.logoUrl);
        await deleteObject(storageRef);

        const settingsDocRef = doc(db, "users", effectiveUserId, "settings", "main");
        await setDoc(settingsDocRef, { logoUrl: "" }, { merge: true });
        
        setUserSettings(prev => ({ ...prev, logoUrl: "" }));
        setLogoFile(null);
        setLogoPreview(null);

    } catch (err) {
        setLogoError("שגיאה במחיקת קובץ: " + err.message);
        console.error("Error removing logo:", err);
    } finally {
        setIsUploading(false);
    }
};
        return (
        <div>
            {/* לוגו/וידאו ראשי עם עריכה במצב עריכה */}
            <div className="flex flex-col items-center mb-6">
                {isEditMode ? (
                    <div className="w-1/2 mx-auto mb-2 flex flex-col items-center">
                        {logoPreview ? (
                            logoPreview.endsWith('.mp4') || (logoFile && logoFile.type === 'video/mp4') ? (
                                <video src={logoPreview} className="w-full mb-2 rounded-lg shadow-lg" controls loop muted playsInline />
                            ) : (
                                <img src={logoPreview} alt="לוגו" className="w-full mb-2 rounded-lg shadow-lg" />
                            )
                        ) : (
                            <div className="w-full h-32 flex items-center justify-center bg-gray-700 rounded-lg text-gray-400 mb-2">לא נבחר לוגו/וידאו</div>
                        )}
                        <input type="file" accept="image/png,image/jpeg,video/mp4" onChange={handleLogoChange} className="block w-full text-sm text-gray-300 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-amber-50 file:text-amber-700 hover:file:bg-amber-100" />
                        {logoError && <div className="text-red-400 mt-2">{logoError}</div>}
                        <div className="flex gap-2 mt-2">
                            {logoFile && <button onClick={handleUploadLogo} disabled={isUploading} className="bg-green-600 hover:bg-green-700 text-white px-4 py-1 rounded">{isUploading ? "מעלה..." : "שמור לוגו"}</button>}
                            {logoPreview && <button onClick={handleRemoveLogo} disabled={isUploading} className="bg-red-500 hover:bg-red-600 text-white px-4 py-1 rounded">מחק לוגו</button>}
                        </div>

                        {/* חיפוש לוגו אוטומטי */}
                        {showLogoFinder && (
                            <div className="mt-4 w-full">
                                <h3 className="text-lg font-semibold mb-2">חיפוש לוגו אוטומטי</h3>
                                <WebLogoFind
                                    onImageSelect={async (imageUrl) => {
                                        if (!effectiveUserId) return;
                                        const docRef = doc(db, "users", effectiveUserId, "settings", "main");
                                        try {
                                            await setDoc(docRef, { logoUrl: imageUrl }, { merge: true });
                                            setLogoPreview(imageUrl);
                                            alert("הלוגו נשמר בהצלחה!");
                                        } catch (error) {
                                            console.error("Error saving logo:", error);
                                            alert("שגיאה בשמירת הלוגו.");
                                        }
                                    }}
                                    storeName={userSettings?.shopName || ""}
                                />
                            </div>
                        )}
                    </div>
                ) : (
                    logoPreview ? (
                        logoPreview.endsWith('.mp4') ? (
                            <video src={logoPreview} className="w-1/2 mx-auto mb-6 rounded-lg shadow-lg" autoPlay loop muted playsInline />
                        ) : (
                            <img src={logoPreview} alt="לוגו" className="w-1/2 mx-auto mb-6 rounded-lg shadow-lg" />
                        )
                    ) : (
                        <video src="/LogoVideo.mp4" className="w-1/2 mx-auto mb-6 rounded-lg shadow-lg" autoPlay loop muted playsInline>הדפדן שלך אינו תומך בתגית הווידאו.</video>
                    )
                )}
            </div>
            <div className="flex flex-col items-center mb-6">
                {/* ... לוגו ... */}
                <div className="w-full flex justify-center">
                    {isEditMode ? (
                        <button
                            className="mt-2 text-white text-center font-bold bg-transparent hover:bg-gray-700 transition-colors"
                            onClick={() => setEditModal({ open: true, type: "shopName" })}
                            style={{ fontSize: '2.5em', WebkitTextStroke: '1px black', outline: 'none', fontFamily: 'Calibri, sans-serif' }}
                        >
                            {userSettings?.shopName || "הכנס שם חנות"}
                        </button>
                    ) : (
                        <div
                            className="mt-2 text-white text-center font-bold bg-transparent"
                            style={{ fontSize: '2.5em', WebkitTextStroke: '1px black', fontFamily: 'Calibri, sans-serif' }}
                        >
                            {userSettings?.shopName || "הכנס שם חנות"}
                        </div>
                    )}
                </div>
            </div>
{showTextboxFeature && (
        <TextBox 
            effectiveUserId={effectiveUserId} 
            backgroundColor={backgroundColor}
    isEditMode={isEditMode}
            storeMessage={storeMessage} 
        />
    )}

    {isEditMode && (
        <BackgroundEditor 
            effectiveUserId={effectiveUserId}
            currentBackground={backgroundColor}
            onBackgroundChange={setBackgroundColor}
        />
    )}


    {/* הצג את רכיב הנדל"ן אם הוא פעיל */}
    {showNadlanFeature && (
        <Nadlan 
            effectiveUserId={effectiveUserId} 
            isEditMode={isEditMode} 
        />
    )}

    {showExcelExport && (
        <ExcelExport 
            effectiveUserId={effectiveUserId} 
            isEditMode={isEditMode} 
        />
    )}

    {/* הצג את התפריט אם הוא פעיל */}
    {menuConfig.showAllMenu && (
        <>
            <div className="grid grid-cols-1 gap-3 mb-6">
                {menuData.map((menuItem) => {
                    const selectedItemsInCategory = expandableSelections.filter(sel => sel.parentId === menuItem.id);
                    const categoryItemCount = selectedItemsInCategory.reduce((sum, sel) => sum + sel.quantity, 0);
                    return (<MenuCategoryItem key={menuItem.id} item={menuItem} isOpen={openExpandableMenus.includes(menuItem.id)} onToggleExpandableMenu={onToggleExpandableMenu} onAddExpandableItem={onAddExpandableItem} selectedItemsInCategory={selectedItemsInCategory} onIncrementSelectedItem={onIncrementItem} onDecrementSelectedItem={onDecrementItem} onToggleChaminOptionItem={onToggleChaminOption} onStartEditingOwnerItem={onStartEditingOwner} onFinishEditingOwnerItem={onFinishEditingOwner} onHandleOwnerTextChangeItem={onHandleOwnerTextChange} calculateSingleChaminItemPriceFunc={calculateSingleChaminItemPriceFunc} cholentKashrutPreference={cholentKashrut} onSetCholentKashrutPreference={onSetCholentKashrut} onSetItemQuantityFunc={onSetItemQuantity} categoryItemCount={categoryItemCount} mehfoodPotData={menuItem.isSpecialChamin ? mehfoodPotData : undefined} sheeritPotData={menuItem.isSpecialChamin ? sheeritPotData : undefined} showPotsFeature={showPotsFeature} isEditMode={isEditMode} onEditCategory={onEditCategory} onEditOption={onEditOption} onDeleteOption={onDeleteOption} onAddOption={onAddOption} onDeleteCategory={onDeleteCategory} />);
                })}
            </div>

            {isEditMode && menuConfig.showCustomMenu && (
                <div className="mt-4 mb-20">
                    <button
                        onClick={onAddCategory}
                        className="w-full py-3 px-4 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors font-semibold text-lg"
                    >
                        + הוסף קטגוריית מוצרים חדשה
                    </button>
                </div>
            )}
            
            {menuData.length > 0 && (
                <>
                    <OrderSubmitButton totalPrice={totalPrice} onSubmitOrder={onInitiateCheckout} isDisabled={expandableSelections.length === 0} />
                    <button className="mt-3 bg-red-600 hover:bg-red-700 text-white font-bold py-2.5 px-4 rounded-lg w-full transition-colors duration-150 text-base" onClick={onNewOrder}>התחל הזמנה חדשה</button>
                </>
            )}
        </>
    )}



    {/* הצג את בלוק המונית רק אם האופציה פעילה בקונפיגורציה */}
    {showMonit && (
      <div className="my-8">
        <Monit effectiveUserId={effectiveUserId} />
      </div>
    )}




                </div>
    );
}

// /// חלק 12: CholentPotStatus ///
function CholentPotStatus({ kashrutName, fillPercentage, statusText }) {
    const visualFillHeightPercent = Math.max(0, Math.min(100, fillPercentage / 3));
    const visualFillHeightForCss = `${visualFillHeightPercent}%`;
    const fixedBottomOffset = '2.5rem';
    return (
        <div className="flex flex-col items-center p-2 bg-gray-700 bg-opacity-50 rounded-lg shadow-md w-32 md:w-40">
            <div className="relative w-24 h-36 mb-1 bg-gray-600 rounded-md overflow-hidden shadow-inner">
                <img src="/sir.png" alt={`סיר ${kashrutName}`} className="w-full h-full object-contain" />
                <div className="absolute left-[19%] right-[19%] bg-yellow-700 opacity-80 rounded-b-md z-0" style={{ height: visualFillHeightForCss, bottom: fixedBottomOffset }} >
                    <div className="absolute top-0 left-0 right-0 h-[2px] bg-yellow-800 opacity-60"></div>
                </div>
            </div>
            <p className="text-sm font-semibold text-amber-400 mt-1 relative z-10">{kashrutName}</p>
            <p className="text-lg font-bold text-white relative z-10">{fillPercentage}%</p>
            <p className="text-xs text-gray-300 relative z-10">{statusText}</p>
        </div>
    );
}

// /// חלק 13: CholentPotsDisplay ///
function CholentPotsDisplay({ mehfoodPotData, sheeritPotData }) {
    return (
        <div className="bg-no-repeat bg-contain bg-center p-4 md:p-6 my-3" style={{ backgroundImage: "url('/pots_display_frame.png')" }}>
            <div className="flex flex-row justify-around items-center gap-4">
                {mehfoodPotData && (<CholentPotStatus kashrutName="מחפוד" fillPercentage={mehfoodPotData.percentage} statusText={mehfoodPotData.statusText} />)}
                {sheeritPotData && (<CholentPotStatus kashrutName="שארית ישראל" fillPercentage={sheeritPotData.percentage} statusText={sheeritPotData.statusText} />)}
            </div>
        </div>
    );
}

// קומפונטת modal לעריכת ערך
function EditContactModal({ isOpen, onClose, label, value, onSave, type }) {
    const [input, setInput] = useState(value || "");
    useEffect(() => { setInput(value || ""); }, [value]);
    if (!isOpen) return null;
    return (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex justify-center items-center z-50 p-4" onClick={onClose}>
            <div className="bg-gray-800 text-white rounded-xl p-8 shadow-2xl max-w-sm w-full text-center" onClick={e => e.stopPropagation()}>
                <h3 className="text-xl font-bold text-amber-400 mb-4">{type === "shopName" ? "שם החנות" : label}</h3>
                <input
                    className="border border-gray-600 bg-gray-700 text-white p-2.5 rounded mb-6 w-full text-right placeholder-gray-400"
                    value={input}
                    onChange={e => setInput(e.target.value)}
                    autoFocus
                    type={type === "whatsapp" || type === "phone" ? "tel" : "text"}
                    inputMode={type === "whatsapp" || type === "phone" ? "numeric" : undefined}
                />
                <div className="flex gap-2 justify-center">
                    <button onClick={() => onSave(input)} className="bg-amber-500 hover:bg-amber-600 text-black font-bold py-2 px-6 rounded-lg transition-colors duration-150">שמור</button>
                    <button onClick={onClose} className="bg-gray-500 hover:bg-gray-600 text-white font-bold py-2 px-6 rounded-lg transition-colors duration-150">ביטול</button>
                </div>
            </div>
        </div>
    );
}

// /// חלק 7: קומפוננטת App ראשית ///
export default function App({ isEditMode, setIsEditMode }) {
const [user, setUser] = useState(undefined);
const [nameInput, setNameInput] = useState("");
const [notificationMethod, setNotificationMethod] = useState('sms');
const [contactInput, setContactInput] = useState('');
const [queueNumber, setQueueNumber] = useState(null);
const [currentTime, setCurrentTime] = useState("");
const [showPassword, setShowPassword] = useState(false);
const [passwordInputState, setPasswordInputState] = useState("");
const [showPopup, setShowPopup] = useState(false);
const [popupMessage, setPopupMessage] = useState("");
const [expandableSelections, setExpandableSelections] = useState([]);
const [nextItemId, setNextItemId] = useState(1);
const [openExpandableMenus, setOpenExpandableMenus] = useState([]);
const [cholentKashrut, setCholentKashrut] = useState("מחפוד");
const [orderFlowStep, setOrderFlowStep] = useState('ordering');
const [mehfoodPotLevel, setMehfoodPotLevel] = useState(MAX_POT_CAPACITY_LITERS);
const [sheeritPotLevel, setSheeritPotLevel] = useState(MAX_POT_CAPACITY_LITERS);
const navigate = useNavigate();
const params = useParams();
const shopIdFromUrl = params.shopId;
const isCustomerMode = !user && !!shopIdFromUrl;
const effectiveUserId = isCustomerMode ? shopIdFromUrl : user?.uid;
const [dingSound, setDingSound] = useState(null);
const [tickSound, setTickSound] = useState(null);
    // --- NEW STATES FOR SHOP SETTINGS ---
const [userSettings, setUserSettings] = useState(null);
const [automationSettings, setAutomationSettings] = useState(null);
const [isTakeaway, setIsTakeaway] = useState(false);
const [showPotsFeature, setShowPotsFeature] = useState(true);
const [editModal, setEditModal] = useState({ open: false, type: null });
const [contactLinks, setContactLinks] = useState({ waze: "", phone: "", whatsapp: "" });
const [menu, setMenu] = useState(null);
const [showTextboxFeature, setShowTextboxFeature] = useState(true);
const [storeMessage, setStoreMessage] = useState("");
const [backgroundColor, setBackgroundColor] = useState('bg-gray-900');
    const [showNadlanFeature, setShowNadlanFeature] = useState(true);
    const [showLogoFinder, setShowLogoFinder] = useState(true);
    const [showExcelExport, setShowExcelExport] = useState(true);
    const [showMonit, setShowMonit] = useState(false);
const [displayExcelModule, setDisplayExcelModule] = useState(true);

// --- הוסף את כל ה-useEffect הזה ---
useEffect(() => {
    if (!effectiveUserId) return;

    const menuRef = doc(db, "users", effectiveUserId, "menu", "main");
    const unsubscribe = onSnapshot(menuRef, (docSnap) => {
        if (docSnap.exists() && docSnap.data().items) {
            // אם קיים מסמך תפריט, טוענים אותו ל-state
            setMenu(docSnap.data().items);
        } else {
            // אם לא קיים מסמך, יוצרים אותו עם תפריט ברירת המחדל
            setDoc(menuRef, { items: DEFAULT_MENU })
                .then(() => {
                    setMenu(DEFAULT_MENU);
                })
                .catch(error => {
                    console.error("Error creating default menu:", error);
                });
        }
    });

    return () => unsubscribe();
}, [effectiveUserId]);


// Global menu configuration
const [menuConfig, setMenuConfig] = useState({
    showAllMenu: true,
    showChaminMenu: true,
    showYaapchikMenu: true,
    showBreadMenu: true,
    showLiverMenu: true,
    showDrinksMenu: true,
    showKugelMenu: true,
    showCustomMenu: true
});

const [menuEditModal, setMenuEditModal] = useState({ isOpen: false, type: null, data: {} });

    // Hooks
useEffect(() => {
    // אם אין מזהה חנות ב-URL, אל תעשה כלום
    if (!effectiveUserId) return;

    // הנתיב החדש משתמש ב-shopId הדינמי
const mehfoodPotRef = doc(db, "users", effectiveUserId, "pots", "mehfood");

  const sheeritPotRef = doc(db, "users", effectiveUserId, "pots", "sheeritYisrael");


    const unsubMehfood = onSnapshot(mehfoodPotRef, (docSnap) => {
        if (docSnap.exists()) { 
            setMehfoodPotLevel(docSnap.data().currentLevelLiters); 
        } else { 
            // אם המסמך לא קיים בטעות, ניצור אותו
            setDoc(mehfoodPotRef, { currentLevelLiters: MAX_POT_CAPACITY_LITERS }); 
        }
    });

    const unsubSheerit = onSnapshot(sheeritPotRef, (docSnap) => {
        if (docSnap.exists()) { 
            setSheeritPotLevel(docSnap.data().currentLevelLiters); 
        } else { 
            setDoc(sheeritPotRef, { currentLevelLiters: MAX_POT_CAPACITY_LITERS }); 
        }
    });

    return () => { 
        unsubMehfood(); 
        unsubSheerit(); 
    };
}, [effectiveUserId]);


    useEffect(() => {
        if (typeof Audio !== "undefined") {
            try {
                setDingSound(new Audio('/ding.mp3'));
                setTickSound(new Audio('/tick.mp3'));
            } catch (error) {
                console.warn("Could not load audio files:", error);
                // Set to null if audio files don't exist
                setDingSound(null);
                setTickSound(null);
            }
        }
    }, []);

    // שעון
    useEffect(() => {
        const updateTime = () => {
            const now = new Date();
            const timeString = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
            setCurrentTime(timeString);
        };
        updateTime();
        const interval = setInterval(updateTime, 60000);
        return () => clearInterval(interval);
    }, []);




// --- NEW useEffect to listen for AUTH state and THEN for shop settings ---
useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
        console.log("[DEBUG] onAuthStateChanged user:", currentUser);
        setUser(currentUser);
    });
    return () => unsubscribe();
}, []);

useEffect(() => {
    if (!effectiveUserId) {
        console.log("[DEBUG] No user/shopId, skipping settings listener");
        return;
    }
    const docRef = doc(db, "users", effectiveUserId, "settings", "main");
    const unsubscribe = onSnapshot(docRef, async (docSnap) => {
        if (docSnap.exists()) {
            const data = docSnap.data();
           const newSettings = {
                ...data.status, // מכיל את isOpen, reopenMessage וכו'.
                shopName: data.shopName || "", // מוסיפים את שם החנות
                logoUrl: data.logoUrl || "" // כדאי להוסיף גם את הלוגו לאותו סטייט
            };
            setUserSettings(newSettings);
            setAutomationSettings(data.automation);
setStoreMessage(data.storeMessage || "");

if (data.display?.backgroundColor) {
    setBackgroundColor(data.display.backgroundColor);
} else {
    setBackgroundColor('bg-gray-900');
}

            if (data.display && typeof data.display.showPots === 'boolean') {
                setShowPotsFeature(data.display.showPots);



            } else {
                setShowPotsFeature(true);
            }
            setContactLinks({
                waze: data.contactWaze || "",
                phone: data.contactPhone || "",
                whatsapp: data.contactWhatsapp || ""
            });
        } else {
            const defaultSettings = {
                status: { isOpen: true, reopenMessage: "החנות סגורה כרגע." },
                automation: { isEnabled: false, openDay: '5', openTime: '10:00', closeDay: '5', closeTime: '15:00' },
                display: { showPots: true }
            };
            await setDoc(docRef, defaultSettings);
            setUserSettings(defaultSettings.status);
            setAutomationSettings(defaultSettings.automation);
            setShowPotsFeature(true);
            setContactLinks({
                waze: "",
                phone: "",
                whatsapp: ""
            });
        }
    });
    return () => unsubscribe();
}, [effectiveUserId]);

useEffect(() => {
    console.log("[DEBUG] userSettings:", userSettings);
    console.log("[DEBUG] automationSettings:", automationSettings);
}, [userSettings, automationSettings]);

// Automation logic (client-side)
useEffect(() => {
    if (!automationSettings || !automationSettings.isEnabled || !user) return;
    const checkAndUpdateStoreStatus = async () => {
        const now = new Date();
        const currentDay = now.getDay(); // 0=Sunday, 1=Monday, ...
        const currentTime = now.toTimeString().slice(0,5); // 'HH:MM'
        const openDay = parseInt(automationSettings.openDay, 10);
        const closeDay = parseInt(automationSettings.closeDay, 10);
        const openTime = automationSettings.openTime;
        const closeTime = automationSettings.closeTime;
        console.log('[DEBUG] Automation:', { currentDay, currentTime, openDay, openTime, closeDay, closeTime });

        // Logic: is store open?
        let isOpen = false;
        if (openDay < closeDay || (openDay === closeDay && openTime < closeTime)) {
            // רגיל: פתיחה וסגירה באותו שבוע
            if (
                (currentDay > openDay && currentDay < closeDay) ||
                (currentDay === openDay && currentTime >= openTime) ||
                (currentDay === closeDay && currentTime < closeTime)
            ) {
                isOpen = true;
            }
        } else {
            // חוצה שבוע (למשל חמישי עד שני)
            if (
                (currentDay > openDay || currentDay < closeDay) ||
                (currentDay === openDay && currentTime >= openTime) ||
                (currentDay === closeDay && currentTime < closeTime)
            ) {
                isOpen = true;
            }
        }
        console.log('[DEBUG] Automation result: isOpen =', isOpen);
        // Update Firestore if status changed
        if (userSettings && userSettings.isOpen !== isOpen) {
            await setDoc(
                doc(db, "users", user.uid, "settings", "main"),
                { status: { ...userSettings, isOpen } },
                { merge: true }
            );
            console.log('[DEBUG] Firestore status updated to:', isOpen);
        }
    };
    checkAndUpdateStoreStatus();
}, [automationSettings, user, userSettings]);

// Listen to global menu configuration


useEffect(() => {
    const configRef = doc(db, "config", "global");
    const unsubscribe = onSnapshot(configRef, (docSnap) => {
        if (docSnap.exists()) {
            const data = docSnap.data();

            // עדכון הגדרות התפריט
            setMenuConfig({
                showAllMenu: data.showAllMenu ?? true,
                showChaminMenu: data.showChaminMenu ?? true,
                showYaapchikMenu: data.showYaapchikMenu ?? true,
                showBreadMenu: data.showBreadMenu ?? true,
                showLiverMenu: data.showLiverMenu ?? true,
                showDrinksMenu: data.showDrinksMenu ?? true,
                showKugelMenu: data.showKugelMenu ?? true,
                showCustomMenu: data.showCustomMenu ?? true
            });

            // עדכון הגדרות תיבת הטקסט
            if (typeof data.showStoreMessageTextbox === 'boolean') {
                setShowTextboxFeature(data.showStoreMessageTextbox);
            } else {
                setShowTextboxFeature(true);
            }

            // עדכון הגדרות רכיב הנדל"ן
            if (typeof data.showNadlanFeature === 'boolean') {
                setShowNadlanFeature(data.showNadlanFeature);
            } else {
                setShowNadlanFeature(true);
            }

            // עדכון הגדרות חיפוש לוגו
            if (typeof data.showLogoFinder === 'boolean') {
                setShowLogoFinder(data.showLogoFinder);
            } else {
                setShowLogoFinder(true);
            }

            // עדכון הגדרות יבוא אקסל
                    if (typeof data.showExcelExport === 'boolean') {
            setShowExcelExport(data.showExcelExport);
        } else {
            setShowExcelExport(true);
        }
        if (typeof data.showMonit === 'boolean') {
            setShowMonit(data.showMonit);
        } else {
            setShowMonit(false);
        }
        }
    });
    return () => unsubscribe();
}, []);

// Filter menu based on configuration
const filteredMenu = useMemo(() => {
    if (!menu) return []; // הגנה בזמן טעינה
    return menu.filter(item => {
        if (!menuConfig.showAllMenu) return false;
        if (item.isSpecialChamin && !menuConfig.showChaminMenu) return false;
        if (item.type === "yaapchik" && !menuConfig.showYaapchikMenu) return false;
        if (item.type === "bread" && !menuConfig.showBreadMenu) return false;
        if (item.type === "liver" && !menuConfig.showLiverMenu) return false;
        if (item.type === "drinks" && !menuConfig.showDrinksMenu) return false;
        if (item.type === "kugel" && !menuConfig.showKugelMenu) return false;
        if (item.type === "custom" && !menuConfig.showCustomMenu) return false;
        return true;
    });
}, [menu, menuConfig]); // הוספת 'menu' כתלות

 const handleLogout = () => {
        const auth = getAuth();
        signOut(auth).then(() => {
            // ההתנתקות הצליחה.
            // onAuthStateChanged יזהה את זה אוטומטית, יעביר את user ל-null,
            // והאפליקציה תחזור לדף הלוגין.
            console.log("User signed out successfully");
        }).catch((error) => {
            console.error("Sign out error", error);
            alert("אירעה שגיאה בזמן ההתנתקות.");
        });
    };

    // Handlers and Functions
    const playDingSound = () => { if (dingSound) dingSound.play().catch(e => console.error("Error playing ding sound:", e)); };
    const playTickSound = () => { if (tickSound) { tickSound.currentTime = 0; tickSound.play().catch(e => console.error("Error playing tick sound:", e)); } };

    const resetOrderState = () => {
        setExpandableSelections([]);
        setNameInput("");
        setContactInput("");
        setNotificationMethod('sms');
        setOpenExpandableMenus([]);
        setCholentKashrut("מחפוד");
        setOrderFlowStep('ordering');
    };

const adminResetPots = async () => {
    playDingSound();
    try {
        await resetPots(effectiveUserId); // Use effectiveUserId instead of undefined SHOP_ID
        setPopupMessage("הסירים התמלאו ואופסו ב-Firebase!");
        setShowPopup(true);
    } catch (error) {
        console.error("Error resetting pots:", error);
        setPopupMessage("שגיאה באיפוס הסירים בשרת.");
        setShowPopup(true);
    }
};

    const toggleExpandableMenu = (itemId) => {
        playTickSound();
        setOpenExpandableMenus(prev => prev.includes(itemId) ? prev.filter(id => id !== itemId) : [...prev, itemId]);
    };

    const addExpandableItem = (parentItem, option) => {
        playTickSound();
        const newItem = {
            id: nextItemId, parentId: parentItem.id, parentType: parentItem.type, quantity: 1, owner: "", isEditingOwner: false,
            ...(parentItem.isSpecialChamin ? { size: option.size, withMeat: true, withKishke: false, kashrut: cholentKashrut } : { label: option.label, price: option.price })
        };
        setExpandableSelections(prev => [...prev, newItem]);
        setNextItemId(p => p + 1);
    };

    const incrementItemQuantity = (itemId) => {
        playTickSound();
        setExpandableSelections(prev => prev.map(s => s.id === itemId ? { ...s, quantity: s.quantity + 1 } : s));
    };

    const decrementItemQuantity = (itemId) => {
        playTickSound();
        const item = expandableSelections.find(s => s.id === itemId);
        if (item && item.quantity > 1) {
            setExpandableSelections(prev => prev.map(s => s.id === itemId ? { ...s, quantity: s.quantity - 1 } : s));
        } else {
            setExpandableSelections(prev => prev.filter(s => s.id !== itemId));
        }
    };
    
    const handleSetItemQuantity = (itemId, quantityStr) => {
        playTickSound();
        const quantity = parseInt(quantityStr, 10);
        if (!isNaN(quantity) && quantity > 0) {
            setExpandableSelections(prev => prev.map(s => (s.id === itemId ? { ...s, quantity } : s)));
        } else if (quantity === 0) {
            decrementItemQuantity(itemId);
        }
    };
    
    const toggleChaminOption = (chaminId, type) => {
        playTickSound();
        setExpandableSelections(prev =>
            prev.map(s => {
                if (s.id === chaminId && s.parentType === 'chamin') {
                    const keyToUpdate = type === 'meat' ? 'withMeat' : 'withKishke';
                    return { ...s, [keyToUpdate]: !s[keyToUpdate] };
                }
                return s;
            })
        );
    };

    const handleSetCholentKashrutPreference = (kashrutValue) => { playTickSound(); setCholentKashrut(kashrutValue); };
    const startEditingOwner = (itemId) => { playTickSound(); setExpandableSelections(prev => prev.map(s => (s.id === itemId ? { ...s, isEditingOwner: true } : s))); };
    const finishEditingOwner = (itemId) => { playTickSound(); setExpandableSelections(prev => prev.map(s => (s.id === itemId ? { ...s, isEditingOwner: false } : s))); };
    const handleOwnerTextChange = (itemId, value) => { setExpandableSelections(prev => prev.map(s => (s.id === itemId ? { ...s, owner: value } : s))); };

  

const calculateSingleChaminItemPrice = (size, withMeat) => {
    if (!menu) return 0; // הגנה בזמן טעינה
    const chaminItem = menu.find(i => i.isSpecialChamin);
    const option = chaminItem?.options.find(o => o.size === size);
    return option ? option.basePrice + (withMeat ? option.meatPrice : 0) : 0;
};

const calculateTotalPrice = () => {
    return expandableSelections.reduce((total, s) => {
        const itemPrice = s.parentType === 'chamin'
            ? calculateSingleChaminItemPrice(s.size, s.withMeat)
            : (s.price || 0);
        return total + (itemPrice * s.quantity);
    }, 0);
};

    const handleInitiateCheckout = () => {
        playTickSound();
        if (userSettings && !userSettings.isOpen) {
            setPopupMessage(userSettings.reopenMessage || "החנות סגורה כרגע. נתראה בקרוב!");
            setShowPopup(true);
            return;
        }
        if (expandableSelections.length === 0) {
            setPopupMessage("נא לבחור מנות להזמנה.");
            setShowPopup(true);
            return;
        }
        const hasCholent = expandableSelections.some(i => i.parentType === 'chamin');
        if (hasCholent) {
            setOrderFlowStep('confirmReceived');
        } else {
            setIsTakeaway(true); 
            setOrderFlowStep('paymentOptions');
        }
    };

    const handleConfirmReceived = (wasReceived) => {
        playTickSound();
        if (wasReceived) {
            setIsTakeaway(true);
            setOrderFlowStep('paymentOptions');
        } else {
            setIsTakeaway(false);
            setOrderFlowStep('getCustomerDetails');
        }
    };
    const handleDetailsSubmittedForPayment = () => {
        playTickSound();
        if (!nameInput || !contactInput) {
            setPopupMessage("נא להזין שם ופרטי יצירת קשר.");
            setShowPopup(true);
            return;
        }
        setOrderFlowStep('paymentOptions');
    };

    const handlePaymentOptionSelected = async (paymentMethod) => {
    playDingSound();
    const currentOrderName = nameInput || "לא צויין";
    if (expandableSelections.length === 0) { 
        setPopupMessage("העגלה ריקה..."); 
        setShowPopup(true); 
        return; 
    }

    const finalPrice = calculateTotalPrice();
    
    // --- בניית אובייקט ההזמנה (נשאר ללא שינוי) ---
    const orderForFirebase = {
        name: currentOrderName,
        contact: contactInput,
        notificationMethod,
        items: expandableSelections,
        totalPrice: finalPrice,
        paymentMethod,
        completedAt: Timestamp.now(), 
    };

    // === החלק המתוקן מתחיל כאן ===
    try {
        // שלב 1: עדכון הסירים תמיד יתבצע אם יש צ'ולנט בהזמנה
        const hasCholent = expandableSelections.some(item => item.parentType === 'chamin');
        if (hasCholent) {
            await runTransaction(db, async (transaction) => {
                let requiredMehfood = 0;
                let requiredSheerit = 0;
                expandableSelections.forEach(item => {
                    if (item.parentType === 'chamin' && item.kashrut) {
                        const consumptionAmount = (CHOLENT_CONSUMPTION_LITERS[item.size] || 0) * item.quantity;
                        if (item.kashrut === "מחפוד") { requiredMehfood += consumptionAmount; } 
                        else { requiredSheerit += consumptionAmount; }
                    }
                });

                if (requiredMehfood > 0) {
                    const potRef = doc(db, "users", effectiveUserId, "pots", "mehfood");
                    const potDoc = await transaction.get(potRef);
                    if (!potDoc.exists()) throw "סיר מחפוד לא קיים!";
                    const currentLevel = potDoc.data().currentLevelLiters;
                    if (currentLevel < requiredMehfood) throw "אין מספיק בסיר מחפוד!";
                    transaction.update(potRef, { currentLevelLiters: currentLevel - requiredMehfood });
                }

                if (requiredSheerit > 0) {
                    const potRef = doc(db, "users", effectiveUserId, "pots", "sheeritYisrael");
                    const potDoc = await transaction.get(potRef);
                    if (!potDoc.exists()) throw "סיר שארית לא קיים!";
                    const currentLevel = potDoc.data().currentLevelLiters;
                    if (currentLevel < requiredSheerit) throw "אין מספיק בסיר שארית!";
                    transaction.update(potRef, { currentLevelLiters: currentLevel - requiredSheerit });
                }
            });
        }

        // שלב 2: שמירת ההזמנה במיקום הנכון (ארכיון או הזמנות פעילות)
        if (isTakeaway) {
            // אם זו הזמנת Takeaway ("כן"), שמור ישירות בארכיון
            await addDoc(collection(db, "users", effectiveUserId, "orderHistory"), orderForFirebase);
            setPopupMessage(`הזמנה הושלמה!`);
            
        } else {
            // אם זו הזמנה רגילה ("לא"), שמור בהזמנות הפעילות ושלח התראה
            const newQueueNum = await addOrder(effectiveUserId, orderForFirebase);
            setQueueNumber(newQueueNum);
            let finalMessage = `הזמנה הושלמה!`;
            if (newQueueNum) { finalMessage += `\nמספר הזמנה לאיסוף: ${newQueueNum}`; }
            setPopupMessage(finalMessage);

            if (contactInput) {
                // ... (קוד שליחת ההתראה נשאר זהה)
                const functions = getFunctions();
                const notifyFunction = httpsCallable(functions, 'sendNotification');
                // ... (הכנת ה-notificationData ושליחתו)
            }
        }
        
        // שלב 3: איפוס המצב בסיום מוצלח
        setShowPopup(true);
        resetOrderState();
        setIsTakeaway(false); // איפוס הזיכרון בכל מקרה

    } catch (error) {
        console.error("Error during order submission:", error);
        setPopupMessage(typeof error === 'string' ? error : "אירעה שגיאה בעיבוד ההזמנה.");
        setShowPopup(true);
    }
};
    const getPotStatusDisplayData = (level) => {
        if (typeof level !== 'number' || isNaN(level) || MAX_POT_CAPACITY_LITERS <= 0) {
            return { percentage: 0, statusText: "שגיאה" };
        }
        const percentage = Math.max(0, Math.min(100, Math.round((level / MAX_POT_CAPACITY_LITERS) * 100)));
        let status = "נגמר";
        if (percentage > 0) {
            status = percentage < 20 ? "גושי" : percentage < 40 ? "סמיך" : "נוזלי";
        }
        return { percentage, statusText: status };
    };
 const handleReturnToOrdering = () => { playTickSound(); setOrderFlowStep('ordering'); };
    const handleAdminAccess = () => { if (passwordInputState === "1234") navigate("/admin"); else alert("סיסמה שגויה"); };
    const handleClosePopup = () => { setShowPopup(false); if (queueNumber) setQueueNumber(null); };
    const handleNewOrder = () => { if (window.confirm("ההזמנה הקיימת תימחק. להמשיך?")) { resetOrderState(); } };
   
    // שמור ערך חדש
    const handleSaveContact = async (type, value) => {
        if (!effectiveUserId) return;
        const docRef = doc(db, "users", effectiveUserId, "settings", "main"); 
 if (type === "shopName") {
 await setDoc(docRef, { shopName: value }, { merge: true });
setUserSettings(prev => ({ ...prev, shopName: value })); 
    setEditModal({ open: false, type: null });
            return;
        }


let update = {};
        if (type === "waze") update.contactWaze = value;
        if (type === "phone") update.contactPhone = value;
        if (type === "whatsapp") update.contactWhatsapp = value;
       
            await setDoc(docRef, update, { merge: true });
    setEditModal({ open: false, type: null });
};


const updateMenuInFirestore = async (newMenu) => {
    if (!effectiveUserId) return;
    const menuRef = doc(db, "users", effectiveUserId, "menu", "main");
    try {
        await setDoc(menuRef, { items: newMenu });
    } catch (error) {
        console.error("Failed to update menu in Firestore:", error);
        alert("שגיאה בעדכון התפריט.");
    }
};

const handleEditCategory = (categoryId) => {
    const category = menu.find(cat => cat.id === categoryId);
    if (category) {
        setMenuEditModal({
            isOpen: true,
            type: 'category',
            data: { categoryId, name: category.name }
        });
    }
};

const handleEditOption = (categoryId, optionIndex) => {
    const category = menu.find(cat => cat.id === categoryId);
    const option = category?.options[optionIndex];
    if (option) {
        setMenuEditModal({
            isOpen: true,
            type: 'option',
            data: { categoryId, optionIndex, ...option }
        });
    }
};

const handleDeleteOption = (categoryId, optionIndex) => {
    if (window.confirm("האם למחוק את המוצר?")) {
        const newMenu = menu.map(category => {
            if (category.id === categoryId) {
                const newOptions = category.options.filter((opt, index) => index !== optionIndex);
                return { ...category, options: newOptions };
            }
            return category;
        });
        setMenu(newMenu);
        updateMenuInFirestore(newMenu);
    }
};

const handleAddOption = (categoryId) => {
    setMenuEditModal({
        isOpen: true,
        type: 'new_option',
        data: { categoryId }
    });
};

const handleSaveMenuItem = (savedData) => {
    const { categoryId, optionIndex } = menuEditModal.data;
    let newMenu;


    if (menuEditModal.type === 'category') {
        newMenu = menu.map(cat => 
            cat.id === categoryId ? { ...cat, name: savedData.name } : cat
        );
    } else if (menuEditModal.type === 'option') {
        newMenu = menu.map(cat => {
            if (cat.id === categoryId) {
                const newOptions = [...cat.options];
                newOptions[optionIndex] = savedData;
                return { ...cat, options: newOptions };
            }
            return cat;
        });
    } else if (menuEditModal.type === 'new_option') {
        newMenu = menu.map(cat => {
            if (cat.id === categoryId) {
                return { ...cat, options: [...cat.options, savedData] };
            }
            return cat;
        });
    }

    if (newMenu) {
        setMenu(newMenu);
        updateMenuInFirestore(newMenu);
    }
    setMenuEditModal({ isOpen: false, type: null, data: {} });
};

const handleAddCategory = () => {
    // מצא את ה-ID הגבוה ביותר הקיים כדי ליצור ID ייחודי חדש
    const maxId = menu.reduce((max, item) => Math.max(item.id, max), 0);

    const newCategory = {
        id: maxId + 1,
        name: "קטגוריה חדשה",
        isExpandable: true,
        isEditable: true, // חשוב: מאפשר עריכה עתידית
        type: `custom_${maxId + 1}`, // נותן type ייחודי
        options: [] // מתחיל עם רשימת מוצרים ריקה
    };


    const newMenu = [...menu, newCategory];
    setMenu(newMenu); // עדכון המצב המקומי
    updateMenuInFirestore(newMenu); // שמירה ב-Firebase
};

const handleDeleteCategory = (categoryId) => {
    if (window.confirm("האם למחוק את כל הקטגוריה וכל המוצרים שבתוכה?")) {
        const newMenu = menu.filter(category => category.id !== categoryId);
        setMenu(newMenu);
        updateMenuInFirestore(newMenu);
    }
};

    // שומר 1: אם אין משתמש, הצג דף כניסה
    if (user === undefined) {
        return <div>טוען...</div>;
    }
    if (!user && !shopIdFromUrl) {
        return <LoginPage />;
    }

    // שומר 2: אם הנתונים בטעינה, הצג הודעת טעינה
if (!userSettings || !menu) {
    return <div className="bg-gray-900 min-h-screen flex items-center justify-center text-white">טוען נתונים...</div>;
}

 // החלף את כל ה-return הקיים בגרסה הזו
// ====================== הדבק את כל הקוד מכאן ======================
return (
    <div className={`min-h-screen ${backgroundColor}`}> 
    <div className="p-4 max-w-4xl mx-auto text-center relative" style={{ backgroundImage: "url('/background.jpg')", backgroundAttachment: "fixed" }}>
            {orderFlowStep === 'ordering' && (
                <MainOrderView
                    currentTime={currentTime}
                    showPassword={showPassword}
                    passwordInput={passwordInputState}
                    onToggleShowPassword={() => { playTickSound(); setShowPassword(!showPassword); }}
                    onPasswordInputChange={(e) => setPasswordInputState(e.target.value)}
                    onAdminAccessSubmit={handleAdminAccess}
                    menuData={filteredMenu}
                    openExpandableMenus={openExpandableMenus}
                    expandableSelections={expandableSelections}
                    cholentKashrut={cholentKashrut}
                    onToggleExpandableMenu={toggleExpandableMenu}
                    onAddExpandableItem={addExpandableItem}
                    onIncrementItem={incrementItemQuantity}
                    onDecrementItem={decrementItemQuantity}
                    onSetItemQuantity={handleSetItemQuantity}
                    onToggleChaminOption={toggleChaminOption}
                    onStartEditingOwner={startEditingOwner}
                    onFinishEditingOwner={finishEditingOwner}
                    onHandleOwnerTextChange={handleOwnerTextChange}
                    calculateSingleChaminItemPriceFunc={calculateSingleChaminItemPrice}
                    onSetCholentKashrut={handleSetCholentKashrutPreference}
                    totalPrice={calculateTotalPrice()}
                    onInitiateCheckout={handleInitiateCheckout}
                    onNewOrder={handleNewOrder}
                    mehfoodPotData={getPotStatusDisplayData(mehfoodPotLevel)}
                    sheeritPotData={getPotStatusDisplayData(sheeritPotLevel)}
                    showPotsFeature={showPotsFeature}
                    onLogout={handleLogout}
                    isCustomerMode={isCustomerMode}
                    isEditMode={isEditMode}
                    contactLinks={contactLinks}
                    setEditModal={setEditModal}
                    setIsEditMode={setIsEditMode}
                    userSettings={userSettings}
                    setUserSettings={setUserSettings}
                    effectiveUserId={effectiveUserId}
                    user={user}
                    onEditCategory={handleEditCategory}
                    onEditOption={handleEditOption}
                    onDeleteOption={handleDeleteOption}
                    onAddOption={handleAddOption}
                    onAddCategory={handleAddCategory}
                    onDeleteCategory={handleDeleteCategory}
                    showTextboxFeature={showTextboxFeature} 
                    storeMessage={storeMessage}
                    backgroundColor={backgroundColor}   
                    setBackgroundColor={setBackgroundColor} 
                    showNadlanFeature={showNadlanFeature}
                    showLogoFinder={showLogoFinder}
                    menuConfig={menuConfig}
                    showExcelExport={showExcelExport}
                    showMonit={showMonit}
                />
            )}

            {orderFlowStep === 'confirmReceived' && (
                <ConfirmReceivedDialog
                    onConfirmYes={() => handleConfirmReceived(true)}
                    onConfirmNo={() => handleConfirmReceived(false)}
                />
            )}
            {orderFlowStep === 'getCustomerDetails' && ( 
                <CustomerDetailsScreen 
                    name={nameInput} 
                    onNameChange={(e) => setNameInput(e.target.value)}
                    contact={contactInput}
                    onContactChange={(e) => setContactInput(e.target.value)}
                    method={notificationMethod}
                    onMethodChange={(e) => {
                        setNotificationMethod(e.target.value);
                        setContactInput('');
                    }}
                    onSubmit={handleDetailsSubmittedForPayment} 
                    onReturnToOrdering={handleReturnToOrdering} 
                    totalPrice={calculateTotalPrice()}
                /> 
            )}

            {orderFlowStep === 'paymentOptions' && ( 
                <PaymentOptionsScreen 
                    onSelectPaymentMethod={handlePaymentOptionSelected} 
                    onReturnToOrdering={handleReturnToOrdering} 
                /> 
            )}

            <ConfirmationPopup showPopup={showPopup} popupMessage={popupMessage} onClosePopup={handleClosePopup} />

            {/* בלוק "צרו קשר" נשאר כאן, כפי שביקשת */}
            <div className="mt-8 pt-4 border-t border-gray-700">
              <p className="text-gray-300 mb-4">צרו איתנו קשר</p>
              {(contactLinks.waze || contactLinks.phone) && (
                <div className="text-gray-400 text-center mb-2 text-sm">
                  {contactLinks.waze && <span>{contactLinks.waze}</span>}
                  {contactLinks.waze && contactLinks.phone && <span> | </span>}
                  {contactLinks.phone && <span>{contactLinks.phone}</span>}
                </div>
              )}
              <div className="flex justify-center items-center gap-4">
                {/* Waze */}
                {isEditMode ? (
                  <button type="button" onClick={() => setEditModal({ open: true, type: "waze" })} className="flex flex-col items-center justify-center p-3 hover:bg-gray-600 rounded-lg transition-colors duration-150 w-24 h-24" title="ערוך כתובת Waze">
                    <img src="/waze-icon.png" alt="Waze" className="w-10 h-10 mb-1" />
                    <span className="text-xs text-white">{contactLinks.waze ? contactLinks.waze : "הזן כתובת"}</span>
                  </button>
                ) : (
                  <a href={contactLinks.waze ? `https://waze.com/ul?q=${encodeURIComponent(contactLinks.waze)}&navigate=yes` : "#"} target="_blank" rel="noopener noreferrer" className="flex flex-col items-center justify-center p-3 hover:bg-gray-600 rounded-lg transition-colors duration-150 w-24 h-24" title="נווט אלינו">
                    <img src="/waze-icon.png" alt="Waze" className="w-10 h-10 mb-1" />
                    <span className="text-xs text-white">נווט אלינו</span>
                  </a>
                )}
                {/* Phone */}
                {isEditMode ? (
                  <button type="button" onClick={() => setEditModal({ open: true, type: "phone" })} className="flex flex-col items-center justify-center p-3 hover:bg-gray-600 rounded-lg transition-colors duration-150 w-24 h-24" title="ערוך מספר טלפון">
                    <img src="/phone-icon.png" alt="Phone" className="w-10 h-10 mb-1" />
                    <span className="text-xs text-white">{contactLinks.phone ? contactLinks.phone : "הזן טלפון"}</span>
                  </button>
                ) : (
                  <a href={contactLinks.phone ? `tel:${contactLinks.phone}` : "#"} className="flex flex-col items-center justify-center p-3 hover:bg-gray-600 rounded-lg transition-colors duration-150 w-24 h-24" title="חייג לעסק">
                    <img src="/phone-icon.png" alt="Phone" className="w-10 h-10 mb-1" />
                    <span className="text-xs text-white">חייג עכשיו</span>
                  </a>
                )}
                {/* WhatsApp */}
                {isEditMode ? (
                  <button type="button" onClick={() => setEditModal({ open: true, type: "whatsapp" })} className="flex flex-col items-center justify-center p-3 hover:bg-gray-600 rounded-lg transition-colors duration-150 w-24 h-24" title="ערוך WhatsApp">
                    <img src="/whatsapp-icon.png" alt="WhatsApp" className="w-10 h-10 mb-1" />
                    <span className="text-xs text-white">{contactLinks.whatsapp ? contactLinks.whatsapp : "הזן וואטסאפ"}</span>
                  </button>
                ) : (
                  <a href={contactLinks.whatsapp ? `https://wa.me/${contactLinks.whatsapp.replace(/^0/, '972')}` : "#"} target="_blank" rel="noopener noreferrer" className="flex flex-col items-center justify-center p-3 hover:bg-gray-600 rounded-lg transition-colors duration-150 w-24 h-24" title="שלח הודעה ב-WhatsApp">
                    <img src="/whatsapp-icon.png" alt="WhatsApp" className="w-10 h-10 mb-1" />
                    <span className="text-xs text-white">שלח הודעה</span>
                  </a>
                )}
              </div>
            </div>
          </div>

          <EditContactModal
              isOpen={editModal.open}
              onClose={() => setEditModal({ open: false, type: null })}
              label={editModal.type === "waze" ? "עריכת כתובת Waze" : editModal.type === "phone" ? "עריכת מספר טלפון" : editModal.type === "whatsapp" ? "עריכת WhatsApp" : editModal.type === "shopName" ? "עריכת שם חנות" : ""}
              value={editModal.type === "shopName" ? userSettings?.shopName || "" : editModal.type ? contactLinks[editModal.type] : ""}
              onSave={val => handleSaveContact(editModal.type, val)}
              type={editModal.type}
          />

          <EditMenuModal
              isOpen={menuEditModal.isOpen}
              onClose={() => setMenuEditModal({ isOpen: false, type: null, data: {} })}
              onSave={handleSaveMenuItem}
              itemData={menuEditModal.data}
              editType={menuEditModal.type}
          />
    </div>

);
}